import type { NodeTypeDTOType, WorkflowExecutionType, WorkflowNodeExecutionType, WorkflowType } from '~/types/workflow'

/** Color del badge de un workflow según su estado. */
export type WorkflowStatusColorType = 'success' | 'warning' | 'secondary'

export const workflowStatusLabel = (value: WorkflowType['status']): string => {
  if (value === 'active') return 'Activo'
  if (value === 'inactive') return 'Inactivo'
  return 'Archivado'
}

export const workflowStatusColor = (value: WorkflowType['status']): WorkflowStatusColorType => {
  if (value === 'active') return 'success'
  if (value === 'inactive') return 'warning'
  return 'secondary'
}

export const workflowStatusIcon = (value: WorkflowType['status']): string => {
  if (value === 'active') return 'material-symbols-light:play-arrow-rounded'
  if (value === 'inactive') return 'material-symbols-light:pause-rounded'
  return 'material-symbols-light:archive-outline'
}

/** Etiqueta legible de una ejecución (workflow completo). */
export const executionLabel = (status: WorkflowExecutionType['status']): string => {
  switch (status) {
    case 'succeeded':
      return 'Completado'
    case 'failed':
      return 'Falló'
    case 'running':
      return 'Ejecutando'
    case 'waiting':
      return 'En espera'
    case 'cancelled':
      return 'Cancelada'
    case 'timed_out':
      return 'Tiempo agotado'
    default:
      return 'En cola'
  }
}

/** Icono de una ejecución según su estado. */
export const executionIcon = (status: WorkflowExecutionType['status'] | WorkflowNodeExecutionType['status']): string => {
  switch (status) {
    case 'succeeded':
      return 'material-symbols-light:check-circle-rounded'
    case 'failed':
      return 'material-symbols-light:error-rounded'
    case 'running':
    case 'waiting':
      return 'material-symbols-light:hourglass-top-rounded'
    case 'skipped':
      return 'material-symbols-light:skip-next-rounded'
    case 'cancelled':
    case 'timed_out':
      return 'material-symbols-light:stop-circle-rounded'
    default:
      return 'material-symbols-light:schedule-rounded'
  }
}

/** Color (semántico) de una ejecución según su estado. */
export const executionColor = (status: WorkflowExecutionType['status'] | WorkflowNodeExecutionType['status']): 'success' | 'danger' | 'info' | 'warning' | 'secondary' => {
  switch (status) {
    case 'succeeded':
      return 'success'
    case 'failed':
    case 'timed_out':
    case 'cancelled':
      return 'danger'
    case 'running':
      return 'info'
    case 'waiting':
      return 'warning'
    default:
      return 'secondary'
  }
}

/** Clases Tailwind para pintar un nodo según su estado de ejecución. */
export const nodeExecutionClasses = (status: WorkflowNodeExecutionType['status']): string => {
  switch (status) {
    case 'succeeded':
      return 'border-success/50 bg-success-soft/40 text-success'
    case 'failed':
      return 'border-danger/60 bg-danger-soft/40 text-danger'
    case 'running':
      return 'border-info/50 bg-info-soft/40 text-info'
    case 'waiting':
      return 'border-warning/50 bg-warning-soft/40 text-warning'
    case 'skipped':
      return 'border-border bg-surface-container text-foreground-subtle'
    default:
      return 'border-border bg-surface text-foreground'
  }
}

/** Etiqueta legible del estado de un nodo dentro de una ejecución. */
export const nodeExecutionLabel = (status: WorkflowNodeExecutionType['status']): string => {
  switch (status) {
    case 'succeeded':
      return 'Completado'
    case 'failed':
      return 'Falló'
    case 'running':
      return 'Ejecutando'
    case 'waiting':
      return 'En espera'
    case 'skipped':
      return 'Omitido'
    default:
      return 'Pendiente'
  }
}

/** Icono de categoría para el color del tile del nodo. */
export const categoryColor = (category: NodeTypeDTOType['category']): string => {
  switch (category) {
    case 'trigger':
      return 'bg-success-soft text-success'
    case 'ai':
      return 'bg-primary-soft text-primary'
    case 'logic':
    case 'control':
      return 'bg-warning-soft text-warning'
    case 'data':
      return 'bg-info-soft text-info'
    case 'integration':
      return 'bg-danger-soft text-danger'
    default:
      return 'bg-surface-container text-foreground-muted'
  }
}

/**
 * Los nodos llegan con el set `material-symbols:` del backend, pero el panel
 * admin usa `material-symbols-light:`. Se normaliza aquí para que la
 * iconografía del editor sea la misma que el resto del sistema.
 */
export const nodeIcon = (icon?: string): string => {
  if (!icon) return 'material-symbols-light:box-outline'
  return icon.replace(/^material-symbols:/, 'material-symbols-light:')
}

/**
 * Iconos usados por los nodos del backend (vienen vía API dinámicamente).
 * El escáner de `bun run icons` no los detecta porque no están como literales
 * en el código fuente, así que los declaramos aquí para que se descarguen.
 *
 * backend: material-symbols:* → frontend: material-symbols-light:*
 */
const _backendNodeIcons = [
  'material-symbols-light:hourglass-outline',
  'material-symbols-light:reply',
  'material-symbols-light:data-object',
  'material-symbols-light:merge',
  'material-symbols-light:alt-route',
  'material-symbols-light:transform',
  'material-symbols-light:variable-insert',
  'material-symbols-light:call-split',
  'material-symbols-light:extension-outline',
  'material-symbols-light:webhook',
  'material-symbols-light:stop-circle-outline',
  'material-symbols-light:play-circle-outline',
  'material-symbols-light:schedule-outline',
  'material-symbols-light:mail-outline',
  'material-symbols-light:filter-alt-outline',
  'material-symbols-light:delete-outline',
  'material-symbols-light:edit-outline',
  'material-symbols-light:add-circle-outline',
  'material-symbols-light:database-outline',
  'material-symbols-light:language',
  'material-symbols-light:smart-toy-outline'
] as const

/** Formatea una duración en ms a una cadena corta legible. */
export const formatDuration = (ms?: number | null): string => {
  if (ms == null) return ''
  if (ms < 1000) return `${ms}ms`
  const s = ms / 1000
  if (s < 60) return `${s.toFixed(1)}s`
  const m = Math.floor(s / 60)
  const rs = Math.round(s % 60)
  return `${m}m ${rs}s`
}

/** Fecha relativa corta en español ("hace 2 min"). */
export const relativeTime = (iso: string | Date | null | undefined): string => {
  if (!iso) return '—'
  const date = typeof iso === 'string' ? new Date(iso) : iso
  const diff = Date.now() - date.getTime()
  if (Number.isNaN(diff)) return '—'
  const sec = Math.floor(diff / 1000)
  if (sec < 5) return 'ahora mismo'
  if (sec < 60) return `hace ${sec}s`
  const min = Math.floor(sec / 60)
  if (min < 60) return `hace ${min} min`
  const hours = Math.floor(min / 60)
  if (hours < 24) return `hace ${hours} h`
  const days = Math.floor(hours / 24)
  if (days < 30) return `hace ${days} d`
  return date.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' })
}
